import { useEffect, useRef, useState } from "react"
import { client } from "@/lib/appwrite"
import { Package } from "lucide-react"
import { cn } from "@/lib/utils"

const fetchWithAuth = async (url: string): Promise<{ blobUrl: string; isImage: boolean }> => {
    const headers = (client as unknown as { headers: Record<string, string> }).headers ?? {}
    const response = await fetch(url, { headers })
    if (!response.ok) throw new Error(`${response.status}`)
    const contentType = response.headers.get("Content-Type") ?? ""
    const isImage = contentType.startsWith("image/")
    const blob = await response.blob()
    return { blobUrl: URL.createObjectURL(blob), isImage }
}

interface AuthImageProps {
    src: string
    alt: string
    className?: string
    fallback?: React.ReactNode
    onError?: () => void
}

export const AuthImage = ({ src, alt, className, fallback, onError }: AuthImageProps) => {
    const [blobUrl, setBlobUrl] = useState<string | null>(null)
    const [failed, setFailed] = useState(false)
    // Track the active blob URL in a ref so the cleanup doesn't revoke it prematurely
    const activeBlobRef = useRef<string | null>(null)

    useEffect(() => {
        let cancelled = false

        setFailed(false)
        setBlobUrl(null)

        fetchWithAuth(src)
            .then(({ blobUrl: url, isImage }) => {
                if (cancelled) { URL.revokeObjectURL(url); return }

                if (!isImage) {
                    // Not an image (PDF, etc.) — release the blob and tell the parent
                    URL.revokeObjectURL(url)
                    setFailed(true)
                    onError?.()
                    return
                }

                // Revoke any previously active blob before storing the new one
                if (activeBlobRef.current) URL.revokeObjectURL(activeBlobRef.current)
                activeBlobRef.current = url
                setBlobUrl(url)
            })
            .catch(() => {
                if (!cancelled) { setFailed(true); onError?.() }
            })

        return () => {
            // Mark fetch as cancelled but do NOT revoke activeBlobRef here —
            // the <img> may still be loading it. Revocation happens either when
            // a new blob replaces it (above) or on component unmount (below).
            cancelled = true
        }
    }, [src])

    // Revoke the blob only when the component unmounts
    useEffect(() => {
        return () => {
            if (activeBlobRef.current) URL.revokeObjectURL(activeBlobRef.current)
        }
    }, [])

    if (failed) {
        return fallback ? <>{fallback}</> : (
            <div className={cn("flex items-center justify-center bg-gray-100 dark:bg-gray-800 text-gray-300", className)}>
                <Package className="h-8 w-8" />
            </div>
        )
    }

    if (!blobUrl) {
        return (
            <div className={cn("flex items-center justify-center bg-gray-100 dark:bg-gray-800", className)}>
                <div className="h-5 w-5 rounded-full border-2 border-gray-300 border-t-blue-500 animate-spin" />
            </div>
        )
    }

    return (
        <img
            src={blobUrl}
            alt={alt}
            className={className}
            onError={() => {
                // Fetch succeeded and Content-Type was image/*, but browser still
                // can't render it (corrupted file, unsupported sub-format, etc.)
                setFailed(true)
                onError?.()
            }}
        />
    )
}
